import type { Dispatch, SetStateAction } from "react";
import { DatePicker } from "@/components/DatePicker";
import { SelectField } from "@/components/SelectField";
import { SensitiveField } from "@/components/SensitiveField";
import { formatSSN, ownerDobRules } from "@/lib/formatters";
import {
  OWNER_TITLE_OPTIONS,
  OWNERSHIP_TYPE_OPTIONS,
  US_STATE_OPTIONS,
} from "@/lib/labels";
import type { AddressForm, FieldSetter, OwnerForm } from "./types";
import { AddressFields } from "./AddressFields";
import { FieldWrap } from "./FieldWrap";
import { SameAsToggle } from "./SameAsToggle";
import { TF } from "./TF";

export function OwnershipFields({
  owner,
  setOwnerField,
  ownerAddr,
  setOwnerAddr,
  ownerAddrSame,
  setOwnerAddrSame,
  fieldError,
  required,
  hideSensitive = false,
}: {
  owner: OwnerForm;
  setOwnerField: FieldSetter<OwnerForm>;
  ownerAddr: AddressForm;
  setOwnerAddr: Dispatch<SetStateAction<AddressForm>>;
  ownerAddrSame: boolean;
  setOwnerAddrSame: (v: boolean) => void;
  fieldError: (field: string) => string | undefined;
  required: boolean;
  // Skips SSN and driver's license number -- both are redacted to a
  // masked last-4 at write time (see sharedModules.redact_merchant_for_
  // storage), so there's never a raw value on file to prefill or safely
  // overwrite. Date of birth and DL state are NOT redacted (stored in
  // full) and stay editable either way.
  hideSensitive?: boolean;
}) {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TF
          id="owner.firstName"
          label="Owner first name"
          value={owner.firstName}
          onChange={(v) => setOwnerField("firstName", v)}
          format="name"
          maxLength={100}
          required={required}
          error={fieldError("owner.firstName")}
        />
        <TF
          id="owner.lastName"
          label="Owner last name"
          value={owner.lastName}
          onChange={(v) => setOwnerField("lastName", v)}
          format="name"
          maxLength={100}
          required={required}
          error={fieldError("owner.lastName")}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FieldWrap
          id="owner.ownershipTypeCd"
          label="Ownership type"
          required={required}
        >
          <SelectField
            id="owner.ownershipTypeCd"
            value={owner.ownershipTypeCd}
            options={OWNERSHIP_TYPE_OPTIONS}
            onChange={(v) => setOwnerField("ownershipTypeCd", v)}
          />
        </FieldWrap>
        <FieldWrap
          id="owner.title"
          label="Owner title"
          required={required}
          error={fieldError("owner.title")}
        >
          <SelectField
            id="owner.title"
            value={owner.title}
            options={OWNER_TITLE_OPTIONS}
            onChange={(v) => setOwnerField("title", v)}
            ariaInvalid={!!fieldError("owner.title")}
            ariaDescribedBy={
              fieldError("owner.title") ? "owner.title-err" : undefined
            }
          />
        </FieldWrap>
        <TF
          id="owner.ownershipPct"
          label="Ownership %"
          value={owner.ownershipPct}
          onChange={(v) => setOwnerField("ownershipPct", v)}
          format="integer"
          maxLength={3}
          error={fieldError("owner.ownershipPct")}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TF
          id="owner.email"
          label="Owner email"
          type="email"
          inputMode="email"
          value={owner.email}
          onChange={(v) => setOwnerField("email", v)}
          maxLength={320}
          required={required}
          error={fieldError("owner.email")}
        />
        <TF
          id="owner.phone"
          label="Owner phone"
          value={owner.phone}
          onChange={(v) => setOwnerField("phone", v)}
          format="phone"
          required={required}
          error={fieldError("owner.phone")}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {!hideSensitive && (
          <FieldWrap
            id="owner.ssn"
            label="Owner SSN"
            sensitive
            required={required}
            error={fieldError("owner.ssn")}
          >
            <SensitiveField
              id="owner.ssn"
              value={owner.ssn}
              onChange={(v) => setOwnerField("ssn", formatSSN(v))}
              maxLength={11}
              inputMode="numeric"
              placeholder="123-45-6789"
              ariaInvalid={!!fieldError("owner.ssn")}
              ariaDescribedBy={
                fieldError("owner.ssn") ? "owner.ssn-err" : undefined
              }
            />
          </FieldWrap>
        )}
        <FieldWrap
          id="owner.dateOfBirth"
          label="Date of birth"
          required={required}
          error={fieldError("owner.dateOfBirth")}
        >
          <DatePicker
            id="owner.dateOfBirth"
            value={owner.dateOfBirth}
            onChange={(v) => setOwnerField("dateOfBirth", v)}
            rules={ownerDobRules()}
            error={fieldError("owner.dateOfBirth")}
            required={required}
            ariaInvalid={!!fieldError("owner.dateOfBirth")}
            ariaDescribedBy={
              fieldError("owner.dateOfBirth") ? "owner.dateOfBirth-err" : undefined
            }
          />
        </FieldWrap>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {!hideSensitive && (
          <FieldWrap
            id="owner.driversLicenseNumber"
            label="Driver's license number"
            sensitive
            required={required}
            error={fieldError("owner.driversLicenseNumber")}
          >
            <SensitiveField
              id="owner.driversLicenseNumber"
              value={owner.driversLicenseNumber}
              onChange={(v) => setOwnerField("driversLicenseNumber", v)}
              maxLength={50}
              inputMode="text"
              ariaInvalid={!!fieldError("owner.driversLicenseNumber")}
              ariaDescribedBy={
                fieldError("owner.driversLicenseNumber")
                  ? "owner.driversLicenseNumber-err"
                  : undefined
              }
            />
          </FieldWrap>
        )}
        <FieldWrap
          id="owner.driversLicenseStateCd"
          label="DL state"
          required={required}
          error={fieldError("owner.driversLicenseStateCd")}
        >
          <SelectField
            id="owner.driversLicenseStateCd"
            value={owner.driversLicenseStateCd}
            options={US_STATE_OPTIONS}
            onChange={(v) => setOwnerField("driversLicenseStateCd", v)}
            ariaInvalid={!!fieldError("owner.driversLicenseStateCd")}
            ariaDescribedBy={
              fieldError("owner.driversLicenseStateCd")
                ? "owner.driversLicenseStateCd-err"
                : undefined
            }
          />
        </FieldWrap>
      </div>
      <SameAsToggle
        checked={ownerAddrSame}
        onChange={setOwnerAddrSame}
        label="Owner address is the same as the business address"
        subtitle="Owner address"
      >
        <AddressFields
          prefix="owner"
          value={ownerAddr}
          set={(k, v) => setOwnerAddr((p) => ({ ...p, [k]: v }))}
          errorAt={fieldError}
          required={required}
        />
      </SameAsToggle>
    </>
  );
}
